import React, { useEffect, useState, useCallback, useMemo } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Container,
  Row,
  Card,
  CardHeader,
  Button,
  Col,
  FormGroup,
  Label,
  CardBody,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from 'reactstrap';

import SimpleHeader from '../../../../components//Headers/SimpleHeader';
import Pagination from '../../../../components/Pagination';
import ReactLoader from '../../../../components/Loader';
import ToggleSwitch from '../../../../components/ToggleSwitch/ToggleSwitch';
import useDebounce from '../../../ServicePanel/components/QueueContent/components/Contact/ContactActiveModal/hooks/useDebaunce';
import {
  icon_column_style,
  individual_column_style,
  individual_icon_header_style,
  individual_header_style
} from '../../../../assets/styles/table_custom_style';

import { DEFAULT_PAGE_SIZE } from '../../../SLA/utils/index';

import PriceCatalogService from '../service/priceCatalogService';
import ProductsByPriceCatalogModal from './components/ProductsByPriceCatalogModal';
import { verifyRoles } from 'components/Authentication/VerifyRoles';

const PriceCatalogList = () => {
  const [priceCatalogs, setPriceCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [quantity, setQuantity] = useState(0);
  const [showDisabled, setShowDisable] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isModalProductsByCatalogOpen, setIsModalProductsByCatalogOpen] =
    useState(false);
  const [catalogId, setCatalogId] = useState(0);

  const debouncedSearchValue = useDebounce(searchValue, 1000);

  const getPriceCatalogs = useCallback(
    (selectedPage = 1, q = '') => {
      const params = {
        page: selectedPage || activePage,
        size: DEFAULT_PAGE_SIZE,
        showDisabled,
        query: q
      };

      PriceCatalogService.getPriceCatalogs(params)
        .then((res) => {
          const priceCatalogs = res.data.data.data;
          const quantity = res.data.data.quantity;
          setPriceCatalogs(priceCatalogs);
          setQuantity(quantity);
          if (quantity === 0) {
            toast.info('Nenhum Catálogo de preços encontrado', {
              autoClose: 3000,
              closeOnClick: true
            });
          }
          setLoading(false);
        })
        .catch(() => {
          toast.error('Erro ao listar Catálogos de Preços', {
            autoClose: 3000,
            closeOnClick: true
          });
        });
    },
    [activePage, showDisabled]
  );

  useEffect(() => {
    getPriceCatalogs(activePage, debouncedSearchValue);
  }, [activePage, debouncedSearchValue, getPriceCatalogs]);

  const handlePageChange = useCallback(
    (activePage) => {
      setActivePage(activePage);
      getPriceCatalogs(activePage, debouncedSearchValue);
    },
    [debouncedSearchValue, getPriceCatalogs]
  );

  const goToPage = useCallback(
    (newPage) => {
      handlePageChange(newPage);
    },
    [handlePageChange]
  );

  const handleEnableDisabledPriceCatalog = useCallback(
    async (id, data) => {
      await PriceCatalogService.enableDisablePriceCatalog(id, data)
        .then((res) => {
          getPriceCatalogs();

          toast.success(
            res?.data?.message ?? 'Catálogo de preço atualizado com sucesso',
            {
              autoClose: 3000,
              closeOnClick: true
            }
          );
        })
        .catch((error) => {
          const msg = error?.response?.data?.message;
          toast.error(
            msg ||
              `Erro ao ${
                !data?.enabled ? 'habilitar' : 'desabilitar'
              } Catálogo de Preço`,
            {
              autoClose: 3000,
              closeOnClick: true
            }
          );
        });
    },
    [priceCatalogs, getPriceCatalogs]
  );

  const togglePriceCatalogStatus = useCallback(
    ({ id, isActive }) => {
      const data = { isActive: !isActive };
      try {
        handleEnableDisabledPriceCatalog(id, data);

        setPriceCatalogs((prevPriceCatalogs) =>
          prevPriceCatalogs.map((catalog) =>
            catalog.id === id ? { ...catalog, isActive: !isActive } : catalog
          )
        );
      } catch (error) {
        const msg = error?.response?.data?.message;
        toast.error(
          msg ||
            `Erro ao ${
              !isActive ? 'desabilitar' : 'habilitar'
            } o Catálogo de Preço`
        );
        setPriceCatalogs((prevPriceCatalogs) => prevPriceCatalogs);
      }
    },
    [handleEnableDisabledPriceCatalog, goToPage]
  );

  const columns = useMemo(
    () => [
      {
        dataField: 'name',
        text: 'Nome',
        sort: true,
        style: individual_column_style,
        headerStyle: individual_header_style
      },
      {
        dataField: 'description',
        text: 'Descrição',
        sort: true,
        style: individual_column_style,
        headerStyle: individual_header_style
      },
      {
        dataField: 'products',
        style: icon_column_style,
        headerStyle: individual_icon_header_style,
        text: 'Visualizar Produtos',
        formatter: (_, row) => {
          return (
            <>
              <Button
                color="info"
                size="sm"
                title="Visualizar Produtos"
                onClick={() => {
                  setIsModalProductsByCatalogOpen(true);
                  setCatalogId(row.id);
                }}
              >
                <li className="fas fa-table"></li>
              </Button>
            </>
          );
        }
      },
      {
        dataField: 'edit',
        style: icon_column_style,
        headerStyle: individual_icon_header_style,
        text: 'Editar',
        formatter: (_, row) => {
          return (
            <>
              {verifyRoles(['CRM_PRICE_CATALOGS_EDIT']) ? (
                <Link to={'/admin/crm/priceCatalogForm/' + row.id}>
                  <Button color="info" size="sm">
                    <li className="fas fa-user-edit"></li>
                  </Button>
                </Link>
              ) : null}
            </>
          );
        }
      },
      {
        dataField: 'habilitar/desabilitar',
        style: icon_column_style,
        headerStyle: individual_icon_header_style,
        text: 'Habilitar/Desabilitar',
        formatter: (_, row) => {
          return (
            <>
              {verifyRoles(['CRM_PRICE_CATALOGS_TOGGLE']) ? (
                <ToggleSwitch
                  checked={row.isActive}
                  id={row.id}
                  onChange={() =>
                    togglePriceCatalogStatus({
                      id: row.id,
                      isActive: row.isActive
                    })
                  }
                />
              ) : null}
            </>
          );
        }
      }
    ],
    [togglePriceCatalogStatus]
  );

  const handleShowDisableds = (value) => {
    setShowDisable(value);
    if (value === false) setActivePage(1);
  };

  return (
    <>
      <SimpleHeader
        setCreate="/admin/crm/priceCatalogForm"
        name="Form validation"
        parentName="Forms"
        role="CRM_PRICE_CATALOGS_CREATE"
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">Catálogos de Preços</h3>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md="8">
                      <InputGroup>
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="fas fa-search" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder="Pesquisar"
                          type="text"
                          onChange={(event) =>
                            setSearchValue(event.target.value)
                          }
                        />
                      </InputGroup>
                    </Col>
                    <Col md="4" className="text-right mt-2">
                      <FormGroup check>
                        <Label check>
                          <Input
                            type="checkbox"
                            checked={showDisabled}
                            onChange={() => handleShowDisableds(!showDisabled)}
                          />
                          <span>Mostrar Catálogos Desabilitados</span>
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
                <ToolkitProvider
                  data={priceCatalogs}
                  keyField="id"
                  columns={columns}
                  bootstrap4={true}
                >
                  {(props) => (
                    <div className="py-4 table-responsive">
                      {loading ? (
                        <Row className="justify-content-md-center">
                          <ReactLoader />
                        </Row>
                      ) : (
                        <BootstrapTable
                          columns={props.baseProps.columns}
                          data={props.baseProps.data}
                          keyField="id"
                          bootstrap4={true}
                          bordered={false}
                          classes="align-items-center"
                        />
                      )}
                    </div>
                  )}
                </ToolkitProvider>
                <Pagination
                  activePage={activePage}
                  totalItemsCount={quantity}
                  onChange={handlePageChange.bind(this)}
                />
              </Card>
            </div>
          </div>
        </Row>
      </Container>
      <ProductsByPriceCatalogModal
        isModalOpen={isModalProductsByCatalogOpen}
        onModalToggle={setIsModalProductsByCatalogOpen}
        catalogId={catalogId}
      />
    </>
  );
};

export default PriceCatalogList;
